import { useState } from "react";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiList,
  MiMenu,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { IAreaTypeResponse } from "@miplatform/types";
import {
  mdiHexagonOutline,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { useAreaTypes, usePermissions } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { SectionInfo } from "../../components/SectionInfo/SectionInfo";
import { useAreaTypesViewModel } from "./useAreaTypesViewModel";

export function AreaTypes() {
  const [search, setSearch] = useState("");

  const { navigate } = useRouter();
  const theme = useTheme();
  const debouncedSearch = useDebounce(search);
  const { hasPermission } = usePermissions();

  const {
    areaTypes,
    recentAreaTypes,
    isLoading: isAreaTypesLoading,
    getAreaTypes,
    getRecentAreaTypes,
  } = useAreaTypes();

  const {
    selectedAreaType,
    showDeleteModal,
    refresh,
    isLoading: isViewModelLoading,
    setSelectedAreaType,
    setShowDeleteModal,
    handleDeleteAreaType,
  } = useAreaTypesViewModel();

  const isLoading = isAreaTypesLoading || isViewModelLoading;

  const canCreateAreaType = hasPermission(PERMISSIONS.PLAN.CREATE);
  const canUpdateAreaType = hasPermission(PERMISSIONS.PLAN.UPDATE);
  const canDeleteAreaType = hasPermission(PERMISSIONS.PLAN.DELETE);

  const sections = [
    {
      fieldset: [
        {
          renderValue: (data: IAreaTypeResponse) => <SectionInfo data={data} />,
        },
        {
          label: "Categories",
          field: "categories",
          renderValue: (data: IAreaTypeResponse) =>
            data.categories?.map((c) => c.categoryName).join(", ") || "--",
        },
        {
          label: "Area Type",
          field: "areaDesignation",
          renderValue: (data: IAreaTypeResponse) =>
            data.areaDesignation || "--",
        },
      ],
    },
  ];

  const handleNavigate = (item: IAreaTypeResponse) => {
    if (item.id) {
      navigate(`/estimating/configuration/area-types/${item.id}`);
    }
  };

  const renderItemActions = (item: IAreaTypeResponse) => {
    const actions = [];

    if (canUpdateAreaType) {
      actions.push({
        label: BUTTON_LABELS.EDIT,
        icon: mdiPencilOutline,
        action: () => handleNavigate(item),
      });
    }

    if (canDeleteAreaType) {
      actions.push({
        label: BUTTON_LABELS.DELETE,
        icon: mdiTrashCanOutline,
        action: () => handleOpenDeleteModal(item),
        color: theme.palette.red.main,
      });
    }

    return actions.length > 0 ? (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu data={actions} />
      </Box>
    ) : null;
  };

  const handleOpenDeleteModal = (item: IAreaTypeResponse) => {
    setSelectedAreaType(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedAreaType) {
      handleDeleteAreaType(selectedAreaType);
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <MiBuildPageHeader
        title="Areas"
        loading={isLoading}
        titleIcon={
          <MiIcon path={mdiHexagonOutline} color={theme.palette.primary.main} />
        }
        actionComponent={
          canCreateAreaType && (
            <MiButton
              title="Area Type"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{ path: mdiPlus }}
              onClick={() =>
                navigate("/estimating/configuration/add-area-types")
              }
              data-testid="add-area-type-btn"
            />
          )
        }
        search={search}
        onHandleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
        searchPlaceholder="Search"
        actionContainerStyle={{
          flexWrap: "nowrap",
        }}
      />
      <MiBox>
        <MiList
          viewType="card"
          cardProps={{
            sections,
            renderItemActions,
            onClick: handleNavigate,
            leftCardProps: { fieldset: [] },
          }}
          search={debouncedSearch}
          data={areaTypes}
          loading={isLoading}
          dataProvider={getAreaTypes}
          recentData={recentAreaTypes}
          recentDataProvider={getRecentAreaTypes}
          paginationMode={PAGINATION.SERVER}
          refresh={refresh}
          emptyListProps={{
            text: "No Areas found",
          }}
          data-testid="area-types-list"
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          open={showDeleteModal}
          textPositive={BUTTON_LABELS.DELETE}
          buttonPositiveColor={BUTTON_COLORS.RED}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.red.main,
          }}
          handleNegative={() => setShowDeleteModal(false)}
          handlePositive={handleConfirmDeleteModal}
          description={`You are about to delete ${selectedAreaType?.name}. Are you sure you would like to continue?`}
        />
      </MiBox>
    </>
  );
}
